import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function UserTelegramSettings({ token, user, onClose }) {
  const [status, setStatus] = useState('loading'); // loading, not_linked, linked
  const [linkData, setLinkData] = useState(null);
  const [botUsername, setBotUsername] = useState('mnrNotifybot');
  const [notifications, setNotifications] = useState({
    my_approvals: true,
    my_repairs: true,
    servicing_reminders: true,
    info_requests: true
  });
  const [message, setMessage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/me/telegram`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBotUsername(data.bot_username || 'mnrNotifybot');
        setNotifications(data.telegram_notifications || notifications);
        if (data.telegram_linked && data.telegram_chat_id) {
          setStatus('linked');
        } else {
          setStatus('not_linked');
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
      setStatus('not_linked');
    }
  };

  const generateLinkCode = async () => {
    setGenerating(true);
    setMessage(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/me/telegram/generate-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinkData(data);
        setBotUsername(data.bot_username || botUsername);
      } else {
        // show the server's reason (e.g. the bot is not set up yet) instead of a generic message
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Failed to generate link code' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to generate link code' });
    }
    setGenerating(false);
  };

  const unlinkTelegram = async () => {
    if (!window.confirm('Are you sure you want to unlink your Telegram account?')) return;
    
    setUnlinking(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/me/telegram/unlink`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setStatus('not_linked');
        setLinkData(null);
        setMessage({ type: 'success', text: 'Telegram unlinked successfully' });
      } else {
        // e.g. two-step login is on for this person, so only an admin can reset it
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Failed to unlink' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to unlink' });
    }
    setUnlinking(false);
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('telegram_enabled', 'true');
      formData.append('notify_my_approvals', notifications.my_approvals);
      formData.append('notify_my_repairs', notifications.my_repairs);
      formData.append('notify_servicing_reminders', notifications.servicing_reminders);
      formData.append('notify_info_requests', notifications.info_requests);

      const response = await fetch(`${BACKEND_URL}/api/users/me/telegram`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Notification preferences saved!' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save' });
    }
    setSaving(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  if (status === 'loading') {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={{ padding: '3rem', textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem' }}>📱</span> My Telegram
            </h2>
            <button onClick={onClose} style={closeButtonStyle}>×</button>
          </div>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.875rem' }}>
            {status === 'linked' ? 'Your account is connected' : 'Link your Telegram for instant notifications'}
          </p>
        </div>

        {message && (
          <div style={{
            padding: '0.875rem 1rem',
            margin: '1rem 1rem 0',
            borderRadius: '8px',
            background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            color: message.type === 'success' ? '#065F46' : '#991B1B',
            fontSize: '0.9rem'
          }}>
            {message.text}
          </div>
        )}

        <div style={{ padding: '1.5rem' }}>
          
          {/* NOT LINKED STATE */}
          {status === 'not_linked' && !linkData && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔗</div>
              <h3 style={{ margin: '0 0 0.5rem', color: '#1E293B' }}>Connect Your Telegram</h3>
              <p style={{ color: '#64748B', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                Get instant notifications about repairs, approvals, and servicing reminders.
              </p>
              <button
                onClick={generateLinkCode}
                disabled={generating}
                style={primaryButtonStyle}
                data-testid="link-telegram-btn"
              >
                {generating ? 'Generating...' : '🔗 Link Telegram'}
              </button>
            </div>
          )}

          {/* LINKING INSTRUCTIONS */}
          {status === 'not_linked' && linkData && (
            <div>
              <div style={stepBoxStyle}>
                <div style={stepNumberStyle}>1</div>
                <div>
                  <strong>Open the bot in Telegram</strong>
                  <p style={{ margin: '0.25rem 0 0.75rem', color: '#64748B', fontSize: '0.85rem' }}>
                    Click the button below or search for <strong>@{botUsername}</strong>
                  </p>
                  <a
                    href={linkData.deep_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={telegramLinkStyle}
                    data-testid="open-telegram-btn"
                  >
                    Open @{botUsername}
                  </a>
                </div>
              </div>

              <div style={stepBoxStyle}>
                <div style={stepNumberStyle}>2</div>
                <div>
                  <strong>Send this command to the bot</strong>
                  <p style={{ margin: '0.25rem 0 0.75rem', color: '#64748B', fontSize: '0.85rem' }}>
                    Copy and paste this message to the bot:
                  </p>
                  <div style={codeBoxStyle}>
                    <code style={{ fontSize: '1rem', fontWeight: '600' }}>/start {linkData.code}</code>
                    <button
                      onClick={() => copyToClipboard(`/start ${linkData.code}`)}
                      style={copyButtonStyle}
                      data-testid="copy-code-btn"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
              </div>

              <div style={stepBoxStyle}>
                <div style={stepNumberStyle}>3</div>
                <div>
                  <strong>Done!</strong>
                  <p style={{ margin: '0.25rem 0 0', color: '#64748B', fontSize: '0.85rem' }}>
                    After sending the command, your account will be linked automatically.
                    Refresh this page to see your status.
                  </p>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <button
                  onClick={fetchSettings}
                  style={{ ...secondaryButtonStyle, marginRight: '0.5rem' }}
                >
                  🔄 Check Status
                </button>
                <button
                  onClick={generateLinkCode}
                  style={secondaryButtonStyle}
                >
                  🔁 New Code
                </button>
              </div>

              <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '0.75rem', marginTop: '1rem' }}>
                Code expires in {linkData.expires_in_minutes} minutes
              </p>
            </div>
          )}

          {/* LINKED STATE */}
          {status === 'linked' && (
            <div>
              <div style={{ 
                background: '#D1FAE5', 
                borderRadius: '12px', 
                padding: '1.25rem', 
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '2rem' }}>✅</span>
                <div>
                  <strong style={{ color: '#065F46' }}>Telegram Connected</strong>
                  <p style={{ margin: '0.25rem 0 0', color: '#047857', fontSize: '0.85rem' }}>
                    You'll receive notifications on Telegram
                  </p>
                </div>
              </div>

              {/* Notification Preferences */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#1E293B', fontSize: '0.95rem' }}>
                  🔔 Notification Preferences
                </h4>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {[
                    { key: 'my_approvals', label: 'Approval updates', desc: 'When your repairs are approved or rejected' },
                    { key: 'my_repairs', label: 'Repair status changes', desc: 'Updates on repairs you created' },
                    { key: 'info_requests', label: 'Information requests', desc: 'When someone needs more info' },
                    { key: 'servicing_reminders', label: 'Servicing reminders', desc: 'Vehicle servicing due alerts' }
                  ].map(item => (
                    <label key={item.key} style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={notifications[item.key]}
                        onChange={(e) => setNotifications({...notifications, [item.key]: e.target.checked})}
                        style={checkboxStyle}
                      />
                      <div>
                        <span style={{ fontWeight: '500' }}>{item.label}</span>
                        <span style={{ display: 'block', fontSize: '0.8rem', color: '#64748B' }}>{item.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
                
                <button
                  onClick={saveNotifications}
                  disabled={saving}
                  style={{ ...primaryButtonStyle, marginTop: '1rem', width: '100%' }}
                >
                  {saving ? 'Saving...' : '💾 Save Preferences'}
                </button>
              </div>

              {/* Unlink */}
              <button
                onClick={unlinkTelegram}
                disabled={unlinking}
                style={dangerButtonStyle}
                data-testid="unlink-telegram-btn"
              >
                {unlinking ? 'Unlinking...' : '🔓 Unlink Telegram'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Styles
const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem'
};

const modalStyle = {
  background: 'white',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '450px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
};

const headerStyle = {
  background: 'linear-gradient(135deg, #0088cc 0%, #00a8e8 100%)',
  color: 'white',
  padding: '1.5rem',
  borderRadius: '16px 16px 0 0'
};

const closeButtonStyle = {
  background: 'rgba(255,255,255,0.2)',
  border: 'none',
  color: 'white',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: '1.25rem'
};

const primaryButtonStyle = {
  background: 'linear-gradient(135deg, #0088cc 0%, #00a8e8 100%)',
  border: 'none',
  color: 'white',
  padding: '0.875rem 1.5rem',
  borderRadius: '10px',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(0, 136, 204, 0.3)'
};

const secondaryButtonStyle = {
  background: '#F1F5F9',
  border: '1px solid #E2E8F0',
  color: '#475569',
  padding: '0.625rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '0.875rem'
};

const dangerButtonStyle = {
  background: 'transparent',
  border: '1px solid #FCA5A5',
  color: '#DC2626',
  padding: '0.625rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '0.875rem',
  width: '100%'
};

const stepBoxStyle = {
  display: 'flex',
  gap: '1rem',
  padding: '1rem',
  background: '#F8FAFC',
  borderRadius: '12px',
  marginBottom: '1rem'
};

const stepNumberStyle = {
  width: '28px',
  height: '28px',
  background: '#0088cc',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: '700',
  fontSize: '0.9rem',
  flexShrink: 0
};

const telegramLinkStyle = {
  display: 'inline-block',
  background: '#0088cc',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: '500',
  fontSize: '0.9rem'
};

const codeBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: '#1E293B',
  color: '#22D3EE',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  fontFamily: 'monospace'
};

const copyButtonStyle = {
  background: '#334155',
  border: 'none',
  color: 'white',
  padding: '0.375rem 0.75rem',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.8rem'
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  cursor: 'pointer',
  padding: '0.5rem',
  borderRadius: '8px',
  transition: 'background 0.15s'
};

const checkboxStyle = {
  width: '18px',
  height: '18px',
  marginTop: '2px',
  cursor: 'pointer',
  accentColor: '#0088cc'
};

export default UserTelegramSettings;
